import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  fetchBalances,
  fetchChainState,
  fetchDelegations,
  fetchNft,
  fetchUnbondingDelegations,
  fetchValidatorsList,
} from '@/api'
import { InfiniteListLoadingStates } from '@/common/InfiniteList'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  formatDurationFromSeconds,
  getNftWithdrawableAtomic,
  sumAtomic,
} from '@/helpers'
import { convertAddressPrefix } from '@/helpers/cosmos'
import { useDebouncedValue } from '@/hooks'
import { queryKeys } from '@/queries/keys'

import { useNftInfiniteList } from './nft-infinite-list'
import { useNftMetadataMap } from './nft-metadata'
import { useSelectedNftActions } from './selected-nft-actions'

const MINIMUM_ADDRESS_LENGTH = 20

export const useNftListViewModel = () => {
  const { address, isWalletConnected, nftModuleParams, isModuleAdmin } =
    useKeplrWallet()
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchQuery, setSearchQuery] = useState('')
  const [showMineOnly, setShowMineOnly] = useState(false)
  const [becomeChoiceOpen, setBecomeChoiceOpen] = useState(false)

  const debouncedSearchQuery = useDebouncedValue(searchQuery, { delayMs: 250 })

  const nftListQuery = useNftInfiniteList({
    pagination: { limit: '8' },
    queryOptions: {
      staleTime: 30_000,
      refetchInterval: 30_000,
    },
  })

  const vestingTimeLabel = useMemo(() => {
    // * 5 because each block is ~5 seconds
    return formatDurationFromSeconds(
      Number(nftModuleParams?.total_vesting_time) * 5,
    )
  }, [nftModuleParams?.total_vesting_time])

  const selectedNftAddress = searchParams.get('nft')

  const { data: selectedNft, isSuccess: isSelectedNftFetched } = useQuery({
    queryKey: queryKeys.nft.byAddress(selectedNftAddress),
    enabled: Boolean(selectedNftAddress),
    queryFn: async () => fetchNft(selectedNftAddress!),
    staleTime: 15_000,
  })

  useEffect(() => {
    if (selectedNftAddress && isSelectedNftFetched && !selectedNft) {
      setSearchParams({})
    }
  }, [selectedNftAddress, isSelectedNftFetched, selectedNft, setSearchParams])

  const { data: searchedNft } = useQuery({
    queryKey: queryKeys.nft.byAddress(debouncedSearchQuery),
    enabled:
      Boolean(debouncedSearchQuery) &&
      debouncedSearchQuery.length > MINIMUM_ADDRESS_LENGTH &&
      !selectedNftAddress,
    queryFn: async () => fetchNft(debouncedSearchQuery),
    staleTime: 15_000,
    retry: false,
  })

  useEffect(() => {
    if (searchedNft && !selectedNftAddress) {
      setSearchParams({ nft: searchedNft.address })
      setSearchQuery('')
    }
  }, [searchedNft, selectedNftAddress, setSearchParams])

  const selectNft = useCallback(
    (nftAddress: string) => {
      setSearchParams({ nft: nftAddress })
    },
    [setSearchParams],
  )

  const closeNftModal = useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  const nfts = useMemo(
    () => nftListQuery.data?.pages.flatMap(page => page.nfts) ?? [],
    [nftListQuery.data],
  )

  const loadNextPage = useCallback(() => {
    if (!nftListQuery.hasNextPage) return
    if (nftListQuery.isFetchingNextPage) return
    nftListQuery.fetchNextPage()
  }, [nftListQuery])

  const listLoadingState = useMemo(() => {
    if (nftListQuery.isFetchingNextPage)
      return InfiniteListLoadingStates.NextLoading
    if (nfts.length > 0 && nftListQuery.isError)
      return InfiniteListLoadingStates.NextError
    return InfiniteListLoadingStates.Idle
  }, [nftListQuery.isError, nftListQuery.isFetchingNextPage, nfts.length])

  const metadataMap = useNftMetadataMap(nfts?.map(nft => nft.uri) ?? [])

  const filteredItems = useMemo(() => {
    if (!nfts) return []
    const normalizedSearch = debouncedSearchQuery.trim().toLowerCase()

    return nfts.filter(nft => {
      if (
        showMineOnly &&
        isWalletConnected &&
        address &&
        nft.owner !== address
      ) {
        return false
      }
      if (!normalizedSearch) return true

      const metadata = metadataMap.get(nft.uri)
      const title = metadata?.name?.toLowerCase() ?? ''
      const description = metadata?.description?.toLowerCase() ?? ''
      const nftAddress = nft.address.toLowerCase()
      const ownerAddress = nft.owner.toLowerCase()

      return (
        title.includes(normalizedSearch) ||
        description.includes(normalizedSearch) ||
        nftAddress.includes(normalizedSearch) ||
        ownerAddress.includes(normalizedSearch)
      )
    })
  }, [
    nfts,
    showMineOnly,
    isWalletConnected,
    address,
    debouncedSearchQuery,
    metadataMap,
  ])

  const {
    data: selectedDelegationData,
    isLoading: isLoadingSelectedDelegation,
  } = useQuery({
    queryKey: queryKeys.staking.delegations(selectedNft?.address),
    enabled: Boolean(selectedNft?.address),
    queryFn: async () => fetchDelegations(selectedNft!.address),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const { data: unbondingDelegationsData } = useQuery({
    queryKey: queryKeys.staking.unbondingDelegations(selectedNft?.address),
    enabled: Boolean(selectedNft?.address),
    queryFn: async () => fetchUnbondingDelegations(selectedNft!.address),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const { data: selectedBalancesData } = useQuery({
    queryKey: queryKeys.bank.balances(selectedNft?.address),
    enabled: Boolean(selectedNft?.address),
    queryFn: async () => fetchBalances(selectedNft!.address),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const { data: chainState } = useQuery({
    queryKey: queryKeys.rpc.chainState(),
    queryFn: fetchChainState,
    staleTime: 5_000,
    refetchInterval: 5_000,
  })

  const currentBlock = Number(chainState?.last_block_height ?? 0)

  const selectedHasDelegation = Boolean(
    (selectedDelegationData?.delegation_responses ?? []).length > 0,
  )

  const selectedDelegationInfo = useMemo(() => {
    if (!selectedHasDelegation || !selectedDelegationData) return null
    const first = selectedDelegationData.delegation_responses?.[0]
    if (!first) return null
    const amountAtomic = sumAtomic(
      (selectedDelegationData.delegation_responses ?? []).map(
        r => r.balance?.amount,
      ),
    )
    return {
      validatorAddress: first.delegation?.validator_address ?? '',
      amountAtomic,
      timestamp: first.delegation?.timestamp as string | undefined,
    }
  }, [selectedDelegationData, selectedHasDelegation])

  const selectedNftBalanceAtomic = useMemo(() => {
    const rewardDenom = bridgelessChainInfo.stakeCurrency.coinMinimalDenom
    const balances = selectedBalancesData?.balances ?? []
    return sumAtomic(
      balances.filter(b => b.denom === rewardDenom).map(b => b.amount),
    )
  }, [selectedBalancesData?.balances])

  const selectedNftWithdrawableAtomic = useMemo(() => {
    if (!selectedNft) return '0'
    return getNftWithdrawableAtomic(
      selectedNft,
      selectedBalancesData ? selectedNftBalanceAtomic : null,
    )
  }, [selectedNft, selectedBalancesData, selectedNftBalanceAtomic])

  const vestingPeriodBlocks = Number(nftModuleParams?.vesting_period ?? 0)

  const isOwner = Boolean(selectedNft?.owner === address)

  const { data: validators = [] } = useQuery<
    { operator_address: string; jailed: boolean }[]
  >({
    queryKey: queryKeys.staking.validatorsIsUser(address),
    enabled: Boolean(isWalletConnected && address),
    queryFn: async () => {
      const fullList = await fetchValidatorsList()
      return fullList.validators.map(v => ({
        operator_address: v.operator_address,
        jailed: v.jailed,
      }))
    },
    staleTime: 60_000,
  })

  const userValoper = address ? convertAddressPrefix(address) : null
  const isValidator = Boolean(
    userValoper && validators.some(v => v.operator_address === userValoper),
  )

  const isJailed = Boolean(
    userValoper &&
    validators.find(v => v.operator_address === userValoper)?.jailed,
  )

  const actions = useSelectedNftActions({
    nft: selectedNft ?? null,
    isWalletConnected,
    isOwner,
    balances: selectedBalancesData?.balances,
    hasDelegation: Boolean(selectedDelegationInfo),
    withdrawableAtomic: selectedNftWithdrawableAtomic,
    hasActiveUnbonding: Boolean(
      (unbondingDelegationsData?.unbonding_responses ?? []).length > 0 &&
      (unbondingDelegationsData?.unbonding_responses?.[0]?.entries ?? [])
        .length > 0,
    ),
  })

  return {
    // Data
    nfts,
    filteredItems,
    vestingTimeLabel,
    selectedNft,
    selectedNftAddress,
    selectedNftBalanceAtomic,
    selectedNftWithdrawableAtomic,
    vestingPeriodBlocks,
    selectedDelegationInfo,
    isLoadingSelectedDelegation,
    currentBlock,
    actions,

    // UI Flags
    isLoading: nftListQuery.isLoading,
    isError: nftListQuery.isError,
    listLoadingState,
    hasNextPage: Boolean(nftListQuery.hasNextPage),
    isWalletConnected,
    isModuleAdmin,
    isValidator,
    isJailed,

    // Methods
    loadNextPage,
    refetch: nftListQuery.refetch,
    selectNft,
    closeNftModal,

    // State Accessors
    searchQuery,
    setSearchQuery,
    showMineOnly,
    toggleMineOnly: () => setShowMineOnly(prev => !prev),
    becomeChoiceOpen,
    setBecomeChoiceOpen,
  }
}
